import { ReleaseAndDeployments } from '@agilelab/plugin-wb-builder-common';
import {
  ConfirmDialog,
  customAlertApiRef,
  WbButton,
  WbLink,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { builderDpCommitPermission } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { Typography } from '@material-ui/core';
import { FC, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { panelCatalogApiRef } from '../../../../api';
import { releaseDetailRouteRef } from '../../../../routes';
import { generateUpdateTooltip, getReleaseInfo } from '../../../../utils';
import { useEditorPageContext } from '../../../EditorPage/context/useEditorPageContext';
import { useReleasePageContext } from '../../context/useReleasePageContext';

interface Props {
  release: ReleaseAndDeployments;
}

export const UpdateButton: FC<Props> = ({ release }) => {
  const [openDialog, setOpenDialog] = useState(false);

  const {
    entity,
    isCommitting,
    setIsCommitting,
    isLoading,
    addRelease,
    processorErrors,
  } = useReleasePageContext();

  const { oldDescriptorFound } = useEditorPageContext();

  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);
  const createdFrom = release?.metadata?.createdFrom;
  const setBranches = new Set(
    createdFrom?.map(cf => cf.branch.replace('%2F', '/')),
  );

  const { allowed: canCommit } = usePermission({
    permission: builderDpCommitPermission,
    resourceRef: stringifyEntityRef({
      kind: entity?.kind ?? 'system',
      namespace: 'default',
      name: entity?.metadata.name ?? '',
    }),
  });

  const [_, commitSnapshot] = useAsyncFn(async () => {
    setIsCommitting(true);
    try {
      const newRelease = await panelCatalog.commitSnapshot(
        entity.metadata.name,
        entity.kind,
        createdFrom,
      );
      addRelease({ ...newRelease, deployments: release.deployments });

      const { kind, namespace, name } = getReleaseInfo(newRelease);

      alertApi.post({
        actions: (
          <WbLink
            route={releaseDetailRoute({ kind, namespace, name })}
            text={`Open V${newRelease?.metadata?.version}`}
          />
        ),

        message: 'Draft-Release updated.',
        severity: 'success',
      });
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setIsCommitting(false);
      setOpenDialog(false);
    }
  }, [
    addRelease,
    alertApi,
    entity.metadata.name,
    panelCatalog,
    setIsCommitting,
    release.deployments,
  ]);

  return (
    <>
      <WbTooltip
        title={generateUpdateTooltip(
          canCommit,
          undefined,
          false,
          isLoading,
          processorErrors,
          [...setBranches],
        )}
      >
        <span>
          <WbButton
            label="Update"
            color="primary"
            size="small"
            loading={isCommitting}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setOpenDialog(true);
            }}
            disabled={!canCommit || isLoading || !!processorErrors?.length}
          />
        </span>
      </WbTooltip>
      <ConfirmDialog
        title="A draft release already exists!"
        description={
          <>
            <Typography>
              By updating you will lose the current draft release and a new one
              will be created.
            </Typography>
            <Typography style={{ fontWeight: '700' }}>
              Are you sure you want to proceed with the update?
            </Typography>
            {oldDescriptorFound && (
              <>
                <br />
                <Typography style={{ fontWeight: '700' }}>Note: </Typography>
                <Typography>
                  A new version of descriptor have been found. The release will
                  be created from the last version (that could differ from the
                  one you see here). Click on "Refresh" to see the last version
                  of the descriptor.
                </Typography>
              </>
            )}
          </>
        }
        open={openDialog}
        onConfirm={commitSnapshot}
        onClose={() => setOpenDialog(false)}
        inProgress={isCommitting}
        confirmButtonText="Yes, update"
      />
    </>
  );
};
